"use client";

import BFLLink from "@repo/shared-ui/BFLLink";
import Conditional from "@repo/shared-ui/Conditional";
import {
  selectIsAuthPending,
  selectIsAuthSuccessOrPerpetual,
  selectIsCommonSsoModalVisible,
  useAppSelector,
} from "@repo/global-store";
import { handleHeaderSignInClickDL } from "./HeaderAnalyticsFunction";
import { LOGGED_IN_MY_ACC_URL, NON_LOGGED_IN_MY_ACC_URL } from "../../constants/HeaderConstants";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import dynamic from "next/dynamic";
import { memo, useState, useEffect } from "react";
import FirstName from "./FirstName";

interface HeaderSignInProps {
  renderLoginModal?: boolean;
  headerSignIn?: {
    heading: string;
    key: string;
    loggedInGreeting: string;
    loggedInGreetingWithoutName: string;
    myAccountLoggedInUrl: string;
    myAccountNonLoggedInUrl: string;
    sectionID: string;
    showOnApp: string;
    showOnWeb: string;
    template: string;
  };
}

/**
 * LoginModalContent
 */
const LoginModalContent: any = dynamic(() =>
  import("../Login/LoginModalContent.tsx").then((m) => m.default)
);

const MemoizedLoginModalContent = memo(LoginModalContent);

const SignIn: React.FC<HeaderSignInProps> = ({ headerSignIn, renderLoginModal = true }) => {
  const [isMounted, setIsMounted] = useState(false);

  const isAuthSuccessOrPerpetual = useAppSelector(selectIsAuthSuccessOrPerpetual);

  const isAuthPending = useAppSelector(selectIsAuthPending);
  const isCommonSsoModalVisible = useAppSelector(selectIsCommonSsoModalVisible);

  const signInLabel = headerSignIn?.heading ?? "Sign In";
  // Keep SSR and first client render deterministic to prevent hydration mismatch.
  const hydratedAuthSuccess = isMounted ? isAuthSuccessOrPerpetual : false;
  const hydratedAuthPending = isMounted ? isAuthPending : false;

  // Only show skeleton on client-side to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const destinationUrl = hydratedAuthSuccess
      ? LOGGED_IN_MY_ACC_URL
      : (headerSignIn?.myAccountNonLoggedInUrl ?? NON_LOGGED_IN_MY_ACC_URL);

    // Track analytics before navigation
    handleHeaderSignInClickDL("Header sign in icon", signInLabel, destinationUrl);

    // Navigate after analytics completes
    window.location.href = destinationUrl;
  };

  return (
    <>
      <div className={hydratedAuthSuccess ? "hidden md:block" : ""}>
        <BFLLink
          href={
            hydratedAuthSuccess
              ? LOGGED_IN_MY_ACC_URL
              : (headerSignIn?.myAccountNonLoggedInUrl ?? NON_LOGGED_IN_MY_ACC_URL)
          }
          aria-label={hydratedAuthSuccess ? "My Account, currently logged in" : signInLabel}
          onClick={handleClick}
          className="order-1 mx-1 flex flex-row-reverse items-center gap-1 px-0 pt-[14px] pb-[30px] hover:bg-[#001326] md:order-1 md:mx-2 md:flex md:!flex-col md:!gap-0"
        >
          <i className="icomoon hidden text-2xl before:content-['\e954'] md:block"></i>
          <span className="relative text-xs leading-[14px] whitespace-nowrap after:ml-[4px] after:inline-flex md:after:hidden">
            <div className="hidden md:block">
              <Conditional if={hydratedAuthPending}>
                <BFLSkeleton count={1} height={14} width={40} borderRadius={0} />
              </Conditional>
            </div>

            <Conditional if={!hydratedAuthPending}>
              <>
                <Conditional if={!hydratedAuthSuccess}>
                  <>{signInLabel}</>
                </Conditional>
                <Conditional if={hydratedAuthSuccess}>
                  <FirstName />
                </Conditional>
              </>
            </Conditional>
          </span>
          <i className="icomoon text-[6px] before:content-['\e911'] md:hidden"></i>
          <i className="icomoon text-2xl before:content-['\e954'] md:hidden"></i>
        </BFLLink>
      </div>

      <Conditional if={renderLoginModal && isCommonSsoModalVisible}>
        <MemoizedLoginModalContent />
      </Conditional>
    </>
  );
};
export default SignIn;
